import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Spinner,
  MessageBar,
  MessageBarBody,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Switch,
  Text
} from '@fluentui/react-components';
import {
  Settings24Regular,
  Save24Regular,
  Dismiss24Regular
} from '@fluentui/react-icons';
import { alertsService } from '../../services/alertsService';
import type { AlertThresholdDto, UpdateAlertThresholdItem, QueueThresholdDto, UpdateQueueThresholdItem } from '../../types';

/**
 * Formata um número com separador de milhar pt-BR (1000 → "1.000").
 * Vazio se 0 para permitir digitação livre.
 */
const formatThousands = (n: number): string => {
  if (!n || isNaN(n)) return '0';
  return n.toLocaleString('pt-BR');
};

/**
 * Remove pontos e caracteres não-dígitos, retornando o número.
 */
const parseThousands = (s: string): number => {
  const cleaned = s.replace(/\D/g, '');
  return cleaned ? parseInt(cleaned, 10) : 0;
};

interface ThousandsInputProps {
  value: number;
  onChange: (n: number) => void;
  className?: string;
}

const ThousandsInput: React.FC<ThousandsInputProps> = ({ value, onChange, className }) => (
  <Input
    className={className}
    size="small"
    type="text"
    inputMode="numeric"
    value={formatThousands(value)}
    onChange={(_, data) => onChange(parseThousands(data.value))}
  />
);

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '1200px',
    width: '95vw',
    minHeight: '600px',
  },
  dialogContent: {
    minHeight: '450px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  description: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginBottom: '16px',
  },
  tableContainer: {
    overflowX: 'auto',
    marginBottom: '16px',
  },
  groupHeader: {
    fontWeight: tokens.fontWeightSemibold,
    backgroundColor: tokens.colorNeutralBackground3,
    borderLeft: `2px solid ${tokens.colorNeutralStroke1}`,
    '& > button': {
      justifyContent: 'center',
      width: '100%',
    },
  },
  groupHeaderFirst: {
    fontWeight: tokens.fontWeightSemibold,
    backgroundColor: tokens.colorNeutralBackground3,
    borderLeft: `2px solid ${tokens.colorNeutralStroke1}`,
    '& > button': {
      justifyContent: 'center',
      width: '100%',
    },
  },
  subHeader: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    width: '90px',
    padding: '4px 8px',
    '& > button': {
      justifyContent: 'center',
      width: '100%',
    },
  },
  subHeaderBorderLeft: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    width: '90px',
    padding: '4px 8px',
    borderLeft: `2px solid ${tokens.colorNeutralStroke1}`,
    '& > button': {
      justifyContent: 'center',
      width: '100%',
    },
  },
  headerText: {
    display: 'block',
    width: '100%',
    textAlign: 'center',
  },
  inputGroup: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  smallInput: {
    width: '100px',
    minWidth: '100px',
    '& input': {
      textAlign: 'center',
      padding: '4px 6px',
      fontSize: tokens.fontSizeBase200,
    },
  },
  compactSwitch: {
    transform: 'scale(0.65)',
    transformOrigin: 'left center',
  },
  switchCell: {
    width: '50px',
    paddingLeft: '4px',
    paddingRight: '0',
  },
  dataCell: {
    textAlign: 'center',
    width: '110px',
    paddingTop: '4px',
    paddingBottom: '4px',
    paddingLeft: '8px',
    paddingRight: '8px',
  },
  dataCellBorderLeft: {
    textAlign: 'center',
    width: '110px',
    paddingTop: '4px',
    paddingBottom: '4px',
    paddingLeft: '8px',
    paddingRight: '8px',
    borderLeftWidth: '2px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorNeutralStroke1,
  },
  disabledCell: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground4,
    textAlign: 'center',
  },
  disabledCellBorderLeft: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground4,
    textAlign: 'center',
    borderLeft: `2px solid ${tokens.colorNeutralStroke1}`,
  },
  entityCell: {
    fontWeight: tokens.fontWeightSemibold,
    minWidth: '120px',
    fontSize: tokens.fontSizeBase200,
  },
  actions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
  legend: {
    display: 'flex',
    gap: '16px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginTop: '8px',
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    marginTop: '24px',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sectionSubtitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginBottom: '12px',
  },
});

interface ThresholdConfigModalProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

interface ThresholdState extends AlertThresholdDto {
  isDirty: boolean;
}

interface QueueThresholdState extends QueueThresholdDto {
  isDirty: boolean;
}

export const ThresholdConfigModal: React.FC<ThresholdConfigModalProps> = ({
  open,
  onClose,
  onSaved
}) => {
  const styles = useStyles();

  const [thresholds, setThresholds] = useState<ThresholdState[]>([]);
  const [queueThresholds, setQueueThresholds] = useState<QueueThresholdState[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadThresholds();
    }
  }, [open]);

  const loadThresholds = async () => {
    try {
      setLoading(true);
      setError(null);

      // Carregar ambos em paralelo
      const [entityResponse, queueResponse] = await Promise.all([
        alertsService.getThresholds(),
        alertsService.getQueueThresholds()
      ]);

      setThresholds(
        entityResponse.thresholds.map(t => ({ ...t, isDirty: false }))
      );

      setQueueThresholds(
        queueResponse.thresholds.map(t => ({ ...t, isDirty: false }))
      );
    } catch (err) {
      console.error('Error loading thresholds:', err);
      setError('Erro ao carregar thresholds');
    } finally {
      setLoading(false);
    }
  };

  const updateThreshold = (
    entityType: string,
    field: keyof AlertThresholdDto,
    value: number | boolean
  ) => {
    setThresholds(prev =>
      prev.map(t =>
        t.entityType === entityType
          ? { ...t, [field]: value, isDirty: true }
          : t
      )
    );
  };

  const updateQueueThreshold = (
    metricType: string,
    field: keyof QueueThresholdDto,
    value: number | boolean
  ) => {
    setQueueThresholds(prev =>
      prev.map(t =>
        t.metricType === metricType
          ? { ...t, [field]: value, isDirty: true }
          : t
      )
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const dirtyEntityThresholds = thresholds.filter(t => t.isDirty);
      const dirtyQueueThresholds = queueThresholds.filter(t => t.isDirty);

      if (dirtyEntityThresholds.length === 0 && dirtyQueueThresholds.length === 0) {
        onClose();
        return;
      }

      // Salvar entity thresholds
      if (dirtyEntityThresholds.length > 0) {
        const entityRequest: UpdateAlertThresholdItem[] = dirtyEntityThresholds.map(t => ({
          entityType: t.entityType,
          newThreshold: t.newThreshold,
          newDurationMinutes: t.newDurationMinutes,
          updateThreshold: t.updateThreshold ?? undefined,
          updateDurationMinutes: t.updateDurationMinutes ?? undefined,
          deleteThreshold: t.deleteThreshold,
          deleteDurationMinutes: t.deleteDurationMinutes,
          isEnabled: t.isEnabled
        }));
        await alertsService.updateThresholds({ thresholds: entityRequest });
      }

      // Salvar queue thresholds
      if (dirtyQueueThresholds.length > 0) {
        const queueRequest: UpdateQueueThresholdItem[] = dirtyQueueThresholds.map(t => ({
          metricType: t.metricType,
          threshold: t.threshold,
          durationMinutes: t.durationMinutes,
          isEnabled: t.isEnabled
        }));
        await alertsService.updateQueueThresholds({ thresholds: queueRequest });
      }

      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Error saving thresholds:', err);
      setError('Erro ao salvar thresholds');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  const hasDirtyChanges = thresholds.some(t => t.isDirty) || queueThresholds.some(t => t.isDirty);

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle>
            <div className={styles.header}>
              <Settings24Regular />
              Configuração de Thresholds
            </div>
          </DialogTitle>

          <DialogContent className={styles.dialogContent}>
            <Text className={styles.description}>
              Configure os limites que disparam alertas. Quando a quantidade de operações ultrapassar o threshold
              dentro da janela de tempo (minutos), um alerta será enviado por email.
            </Text>

            {loading ? (
              <Spinner size="medium" label="Carregando..." />
            ) : (
              <>
                {error && (
                  <MessageBar intent="error" style={{ marginBottom: '16px' }}>
                    <MessageBarBody>{error}</MessageBarBody>
                  </MessageBar>
                )}

                <div className={styles.tableContainer}>
                  <Table size="small">
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell rowSpan={2} className={styles.entityCell}>
                          Entidade
                        </TableHeaderCell>
                        <TableHeaderCell rowSpan={2} className={styles.switchCell}>
                          Ativo
                        </TableHeaderCell>
                        <TableHeaderCell colSpan={2} className={styles.groupHeaderFirst}>
                          <span className={styles.headerText}>New</span>
                        </TableHeaderCell>
                        <TableHeaderCell colSpan={2} className={styles.groupHeader}>
                          <span className={styles.headerText}>Update</span>
                        </TableHeaderCell>
                        <TableHeaderCell colSpan={2} className={styles.groupHeader}>
                          <span className={styles.headerText}>Delete</span>
                        </TableHeaderCell>
                      </TableRow>
                      <TableRow>
                        <TableHeaderCell className={styles.subHeaderBorderLeft}>
                          <span className={styles.headerText}>Quantidade</span>
                        </TableHeaderCell>
                        <TableHeaderCell className={styles.subHeader}>
                          <span className={styles.headerText}>Minutos</span>
                        </TableHeaderCell>
                        <TableHeaderCell className={styles.subHeaderBorderLeft}>
                          <span className={styles.headerText}>Quantidade</span>
                        </TableHeaderCell>
                        <TableHeaderCell className={styles.subHeader}>
                          <span className={styles.headerText}>Minutos</span>
                        </TableHeaderCell>
                        <TableHeaderCell className={styles.subHeaderBorderLeft}>
                          <span className={styles.headerText}>Quantidade</span>
                        </TableHeaderCell>
                        <TableHeaderCell className={styles.subHeader}>
                          <span className={styles.headerText}>Minutos</span>
                        </TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {thresholds.map(threshold => (
                        <TableRow key={threshold.entityType}>
                          <TableCell className={styles.entityCell}>
                            {threshold.entityLabel}
                          </TableCell>
                          <TableCell className={styles.switchCell}>
                            <Switch
                              className={styles.compactSwitch}
                              checked={threshold.isEnabled}
                              onChange={(_, data) =>
                                updateThreshold(threshold.entityType, 'isEnabled', !!data.checked)
                              }
                            />
                          </TableCell>

                          {/* New */}
                          <TableCell className={styles.dataCellBorderLeft}>
                            <div className={styles.inputGroup}>
                              <ThousandsInput
                                className={styles.smallInput}
                                value={threshold.newThreshold}
                                onChange={(n) => updateThreshold(threshold.entityType, 'newThreshold', n)}
                              />
                            </div>
                          </TableCell>
                          <TableCell className={styles.dataCell}>
                            <div className={styles.inputGroup}>
                              <Input
                                className={styles.smallInput}
                                size="small"
                                type="number"
                                value={threshold.newDurationMinutes.toString()}
                                onChange={(_, data) =>
                                  updateThreshold(threshold.entityType, 'newDurationMinutes', parseInt(data.value) || 60)
                                }
                              />
                            </div>
                          </TableCell>

                          {/* Update */}
                          {threshold.hasUpdate ? (
                            <>
                              <TableCell className={styles.dataCellBorderLeft}>
                                <div className={styles.inputGroup}>
                                  <ThousandsInput
                                    className={styles.smallInput}
                                    value={threshold.updateThreshold ?? 0}
                                    onChange={(n) => updateThreshold(threshold.entityType, 'updateThreshold', n)}
                                  />
                                </div>
                              </TableCell>
                              <TableCell className={styles.dataCell}>
                                <div className={styles.inputGroup}>
                                  <Input
                                    className={styles.smallInput}
                                    size="small"
                                    type="number"
                                    value={(threshold.updateDurationMinutes ?? 60).toString()}
                                    onChange={(_, data) =>
                                      updateThreshold(threshold.entityType, 'updateDurationMinutes', parseInt(data.value) || 60)
                                    }
                                  />
                                </div>
                              </TableCell>
                            </>
                          ) : (
                            <TableCell colSpan={2} className={styles.disabledCellBorderLeft}>
                              —
                            </TableCell>
                          )}

                          {/* Delete */}
                          <TableCell className={styles.dataCellBorderLeft}>
                            <div className={styles.inputGroup}>
                              <ThousandsInput
                                className={styles.smallInput}
                                value={threshold.deleteThreshold}
                                onChange={(n) => updateThreshold(threshold.entityType, 'deleteThreshold', n)}
                              />
                            </div>
                          </TableCell>
                          <TableCell className={styles.dataCell}>
                            <div className={styles.inputGroup}>
                              <Input
                                className={styles.smallInput}
                                size="small"
                                type="number"
                                value={threshold.deleteDurationMinutes.toString()}
                                onChange={(_, data) =>
                                  updateThreshold(threshold.entityType, 'deleteDurationMinutes', parseInt(data.value) || 60)
                                }
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className={styles.legend}>
                  <span><strong>Quantidade:</strong> Número de operações para disparar alerta</span>
                  <span><strong>Minutos:</strong> Janela de tempo em minutos</span>
                </div>

                {/* Seção: Filas de Processamento */}
                <Text className={styles.sectionTitle}>
                  Filas de Processamento
                </Text>
                <Text className={styles.sectionSubtitle}>
                  Verificação a cada 15 minutos. Alerta disparado quando o valor atual exceder o threshold.
                </Text>

                <div className={styles.tableContainer}>
                  <Table size="small">
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell className={styles.entityCell}>
                          Métrica
                        </TableHeaderCell>
                        <TableHeaderCell className={styles.switchCell}>
                          Ativo
                        </TableHeaderCell>
                        <TableHeaderCell className={styles.dataCell}>
                          <span className={styles.headerText}>Quantidade</span>
                        </TableHeaderCell>
                        <TableHeaderCell className={styles.dataCell}>
                          <span className={styles.headerText}>Minutos (Cooldown)</span>
                        </TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {queueThresholds.map(threshold => (
                        <TableRow key={threshold.metricType}>
                          <TableCell className={styles.entityCell}>
                            {threshold.metricLabel}
                          </TableCell>
                          <TableCell className={styles.switchCell}>
                            <Switch
                              className={styles.compactSwitch}
                              checked={threshold.isEnabled}
                              onChange={(_, data) =>
                                updateQueueThreshold(threshold.metricType, 'isEnabled', !!data.checked)
                              }
                            />
                          </TableCell>
                          <TableCell className={styles.dataCell}>
                            <div className={styles.inputGroup}>
                              <ThousandsInput
                                className={styles.smallInput}
                                value={threshold.threshold}
                                onChange={(n) => updateQueueThreshold(threshold.metricType, 'threshold', n)}
                              />
                            </div>
                          </TableCell>
                          <TableCell className={styles.dataCell}>
                            <div className={styles.inputGroup}>
                              <Input
                                className={styles.smallInput}
                                size="small"
                                type="number"
                                value={threshold.durationMinutes.toString()}
                                onChange={(_, data) =>
                                  updateQueueThreshold(threshold.metricType, 'durationMinutes', parseInt(data.value) || 60)
                                }
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </DialogContent>

          <DialogActions>
            <div className={styles.actions}>
              <Button
                appearance="secondary"
                icon={<Dismiss24Regular />}
                onClick={handleClose}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button
                appearance="primary"
                icon={<Save24Regular />}
                onClick={handleSave}
                disabled={loading || saving || !hasDirtyChanges}
              >
                {saving ? <Spinner size="tiny" /> : 'Salvar'}
              </Button>
            </div>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
